import { AfterViewInit, Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import {
  GeoJSONSource,
  LayerSpecification,
  LineLayerSpecification,
  LngLatBounds,
  Map as MapLibre,
  RasterLayerSpecification,
  RasterSourceSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl';
import { MapLayersManager } from '../../../../../shared/components/map-viewer/map-layers-manager';
import { ConfigurationModel } from '../../../../../shared/models/configuration.model';
import { Subscription } from 'rxjs';
import { ConfigurationService } from '../../../../../configuration/configuration.service';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MapLayerConfiguration, MapLayerType } from '../../../../../shared/models/map-layer.model';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { FeatureCollection } from 'geojson';
import { RoutesService } from '../../../../../data/routes/routes.service';
import { RouteConfigurationWithSchema } from '../../../../../shared/models/route';
import { LambdaService } from '../../../../../data/lambda/lambda.service';

@Component({
  selector: 'app-map-layer-preview',
  templateUrl: './map-layer-preview.component.html',
  styleUrls: ['./map-layer-preview.component.scss'],
  imports: [MatDialogContent, MatDialogTitle, MatCheckbox, FormsModule, MatDialogActions, MatButton, MatDialogClose],
})
export class MapLayerPreviewComponent implements OnInit, AfterViewInit {
  @Input()
  mapTitle = 'layer-preview-map';

  @ViewChild('map') private readonly mapContainer: ElementRef<HTMLElement>;
  map: MapLibre;

  tenantConfig: ConfigurationModel;
  configSubscription: Subscription;
  bounds: LngLatBounds = new LngLatBounds([-105.5, 39], [-105.0, 40]);
  mapLayers: MapLayerConfiguration[];
  errors: string[] = [];
  loaded = false;

  routeConfigurations: RouteConfigurationWithSchema[];
  lineLayerSpecifications = new Map<number, LineLayerSpecification[]>;
  customLayerRouteSource: FeatureCollection;

  private readonly ROUTE_SOURCE_ID = 'routes-source';

  constructor(
    private readonly configurationService: ConfigurationService,
    private readonly routeService: RoutesService,
    private readonly lambdaService: LambdaService,
    public dialogRef: MatDialogRef<MapLayerPreviewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { mapLayers: MapLayerConfiguration[], customerId: number },
  ) {}

  ngOnInit(): void {
    this.mapLayers = this.data.mapLayers;
    const routeMapLayers = this.mapLayers.filter(layer => layer.type === MapLayerType.ROUTE);
    if (routeMapLayers.length > 0) {
      // get route configurations
      this.routeService.getRouteConfigurations().then(response => {
        this.routeConfigurations = response.data;

        // translate to LineLayerSpecification[]
        routeMapLayers.forEach((mapLayer: MapLayerConfiguration) => {
          const routeConfig = this.routeConfigurations.find(config => config.id === mapLayer.routeConfigurationId);
          if (routeConfig) {
            this.lambdaService.routeConfigToLineLayerSpecifications(routeConfig).then(response => {
              this.lineLayerSpecifications[routeConfig.id] = response;
            });
          } else {
            console.error('Missing route configuration for map layer: ', mapLayer.name);
          }
        });
      });

      // pull source data
      this.routeService.getPublicCustomLayersRouteGeoJson(this.data.customerId).then(response => {
        this.customLayerRouteSource = response;
        this.updateRouteSourceData();
      });
    }
    this.configSubscription = this.configurationService.sharedConfigurationModel.subscribe(configuration => {
      if (configuration) {
        this.tenantConfig = configuration;
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.tenantConfig) {
      const configuration = this.tenantConfig;
      if (configuration.region.length > 0) {
        this.bounds = configuration.region.reduce(
          (bounds, latLngModel) => {
            return bounds.extend([latLngModel.lng, latLngModel.lat]);
          },
          new LngLatBounds(configuration.region[0], configuration.region[0])
        );

        if (this.map) {
          this.map.setMaxBounds(this.bounds);
        }
      } else {
        console.warn('Tenant Region is empty, leaving default bounds.');
      }
    }

    this.loadMap();
  }

  loadMap() {
    const useMetric = this.tenantConfig?.useMetricSystem;
    this.map = new MapLibre({
      container: this.mapContainer.nativeElement,
      style: useMetric ? MapLayersManager.ACCUTERRA_WINTER_M_MAP_STYLE : MapLayersManager.ACCUTERRA_WINTER_MAP_STYLE,
      bounds: this.bounds,
    });

    this.map.on('load', () => {
      this.map.addSource(this.ROUTE_SOURCE_ID, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        }
      });
      this.updateRouteSourceData();

      // load additional layers
      this.mapLayers.forEach(mapLayer => {
        this.addOverlayMapLayer(mapLayer);
      });
      const canvasElement = this.map.getCanvas();
      canvasElement.setAttribute('aria-label', this.mapTitle);
      this.loaded = true;
    });
  }

  toggleMapLayer(layerInfo: MapLayerConfiguration) {
    if (layerInfo.type === MapLayerType.RASTER) {
      if (this.map.getLayer(layerInfo.name)) {
        this.map.setLayoutProperty(layerInfo.name, 'visibility', layerInfo.checked ? 'visible' : 'none');
      }
    } else if (layerInfo.type === MapLayerType.VECTOR) {
      const vectorLayers = JSON.parse(layerInfo.configuration) as LayerSpecification[];
      vectorLayers.forEach(layer => {
        if (this.map.getLayer(layer.id)) {
          this.map.setLayoutProperty(layer.id, 'visibility', layerInfo.checked ? 'visible' : 'none');
        }
      });
    } else if (layerInfo.type === MapLayerType.ROUTE) {
      this.lineLayerSpecifications[layerInfo.routeConfigurationId]?.forEach(layer => {
        if (this.map.getLayer(layer.id)) {
          this.map.setLayoutProperty(layer.id, 'visibility', layerInfo.checked ? 'visible' : 'none');
        }
      });
    }
  }

  private addOverlayMapLayer(layerInfo: MapLayerConfiguration) {
    if (layerInfo.type === MapLayerType.RASTER) {
      this.addRasterOverlayMapLayer(layerInfo);
    } else if (layerInfo.type === MapLayerType.VECTOR) {
      this.addVectorOverlayMapLayer(layerInfo);
    } else if (layerInfo.type === MapLayerType.ROUTE) {
      this.lineLayerSpecifications[layerInfo.routeConfigurationId]?.forEach(layer => {
        this.map.addLayer(layer);
        this.map.setLayoutProperty(layer.id, 'visibility', layerInfo.checked ? 'visible' : 'none');
      });
    }
  }

  private addRasterOverlayMapLayer(layerInfo: MapLayerConfiguration) {
    const layer = {
      id: layerInfo.name,
      source: `${layerInfo.name}-source`,
      type: 'raster',
      layout: {
        visibility: layerInfo.checked ? 'visible' : 'none',
      },
      paint: {
        'raster-opacity': layerInfo.opacity ?? 1.0,
      },
    } as RasterLayerSpecification;

    const source = {
      type: 'raster',
      tiles: [layerInfo.url],
      tileSize: 256,
    } as RasterSourceSpecification;

    this.map.addSource(`${layerInfo.name}-source`, source);
    this.map.addLayer(layer as LayerSpecification);
  }

  private addVectorOverlayMapLayer(layerInfo: MapLayerConfiguration) {
    let configuration;
    try {
      configuration = JSON.parse(layerInfo.configuration);
    } catch (e: any) {
      this.setError(layerInfo, 'Error in layer configuration.', e);
      return;
    }
    const vectorLayers = configuration as LayerSpecification[];
    if (!vectorLayers || vectorLayers.length === 0) {
      this.setError(layerInfo, 'Empty configuration.', null);
    }
    vectorLayers.forEach(layer => {
      const symbolLayer = layer as SymbolLayerSpecification;
      if (!this.validateSymbolLayerSpecification(layerInfo, symbolLayer)) {
        return;
      }
      this.map.addLayer(symbolLayer);
      if (this.map.getLayer(layer.id)) {
        this.map.setLayoutProperty(layer.id, 'visibility', layerInfo.checked ? 'visible' : 'none');
      }
    });
  }

  private validateSymbolLayerSpecification(layerInfo: MapLayerConfiguration, layer: SymbolLayerSpecification): boolean {
    let valid = true;
    if (!layer.id) {
      this.setError(layerInfo, 'Missing Layer ID in Symbol Layer Specification.', null);
      valid = false;
    }
    if (!layer.type || layer.type !== 'symbol') {
      this.setError(layerInfo, 'Missing or incorrect type in Symbol Layer Specification.', null);
      valid = false;
    }
    if (!layer.source) {
      this.setError(layerInfo, 'Missing source in Symbol Layer Specification.', null);
      valid = false;
    }
    return valid;
  }

  private setError(layerInfo: MapLayerConfiguration, message: string, exception: any) {
    let exceptionMessage = '';
    if (exception) {
      exceptionMessage = `Message: ${exception}`;
    }
    this.errors.push(`Layer ${layerInfo.name}: ${message} ${exceptionMessage}`);
    layerInfo.checked = false;
    layerInfo.disabledOnPreview = true;
  }

  private updateRouteSourceData() {
    if (this.map && this.customLayerRouteSource) {
      const routeSource = this.map.getSource(this.ROUTE_SOURCE_ID) as GeoJSONSource;
      routeSource?.setData(this.customLayerRouteSource);
    }
  }
}
